import { createAction } from '@reduxjs/toolkit'
import assign from 'lodash/assign'
import orderBy from 'lodash/orderBy'
import { trackAddToCart, trackRemoveFromCart } from 'src/common/lib/segment'
import { tagsInitiateJoinRace } from 'src/common/lib/tags/tags'
import { BundledChallenge } from 'src/services/types/joinEventV2'
import {
    clearVouchers,
    updateAddonSelections,
    updateCart,
    updateRewardSelections,
    updateVouchers
} from '.'
import { AppState } from '..'
import {
    salesTaxableSelector,
    salesTaxTotalSelector,
    shippingSelector,
    subTotalSelector,
    totalPrePromotionSelector,
    totalSelector
} from './selectors'
import { emptyCart } from './utils'

// import { showToastSuccess } from 'common/utils'
// import { tagsAddToCart } from '../../../common/tags'

export function prepareCartObject(state: AppState) {
    const challenge = state.joinevent
    const cart = assign({}, challenge.cart)
    const total = totalSelector(state)

    // DV-GRHM: This is needed for percentage discounts / fixes editing an order where a percent discount was used
    cart.vouchers = orderBy(cart.vouchers, ['isPromotion'], ['desc'])
    //TODO: Might not need to set this, once we're pulling the cart in properly
    cart.challengeId = challenge.challengeId
    cart.total = total
    cart.subTotal = subTotalSelector(state)
    cart.totalPrePromotion = totalPrePromotionSelector(state)
    cart.salesTax = salesTaxTotalSelector(state)
    cart.salesTaxable = salesTaxableSelector(state)
    cart.geoCounty = challenge.salesTax.geoCounty
    cart.salesTaxRate = challenge.salesTax.taxSales //This might be useful to know on the server-side
    cart.internationalShipping = shippingSelector(state) //This might be useful to know on the server-side
    cart.items = []
    cart.predefinedGiftCard = { code: '', amount: 0, balance: 0 }
    ;[...challenge.rewards.concat(challenge.addons)]
        .filter((item) => item.selected)
        .map((item) => {
            let description = item.itemName //item.itemDescription ? item.itemDescription : item.itemName;
            if (
                item.challengeAddonOptionName !== null &&
                item.challengeAddonOptionName !== '' &&
                typeof item.challengeAddonOptionName !== undefined
            )
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                description += ' (' + item.challengeAddonOptionName + ')'
            cart.items.push({
                challengeAthleteAddonSelectionId: item.challengeAthleteAddonSelectionId,
                challengeAddonId: item.challengeAddonId,
                challengeAddonOptionId:
                    item.challengeAddonOptionId != null ? item.challengeAddonOptionId : 0,
                description: description,
                itemName: item.itemName,
                itemType: item.itemType,
                itemPrice: item.itemPrice,
                itemSent: item.itemSent,
                status: item.status,
                sentTS: item.sentTS,
                challengeAddonType: item.challengeAddonType
            })
        })

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return cart
}

export const getSalesTax = createAction('joinevent/getSalesTax')

export const updateSelectedAddons = createAction('joinevent/updateSelectedAddons')

export function onInitiateJoinRace() {
    return (getState) => {
        const state: AppState = getState()
        tagsInitiateJoinRace(state.joinevent.summary)
    }
}

export function resetBundle() {
    return (dispatch) => {
        const newCartStateObj = {
            ...emptyCart,
            bundle: emptyCart.bundle
        }
        dispatch(updateCart(newCartStateObj))
    }
}

export function removeFromBundle(challengeId) {
    return (dispatch, getState) => {
        const state: AppState = getState()
        const user = state.user.data

        const newBundleChallengesState = state.joinevent.cart.bundle.challenges.filter(
            (item) => item.key !== challengeId
        )

        const newCartStateObj = {
            ...state.joinevent.cart,
            bundle: {
                ...state.joinevent.cart.bundle,
                challenges: newBundleChallengesState
            }
        }

        const removedChallenges = state.joinevent.cart.bundle.challenges.filter(
            (item) => !newCartStateObj.bundle.challenges.includes(item)
        )

        trackRemoveFromCart(removedChallenges, user)
        dispatch(updateCart(newCartStateObj))
        dispatch(clearVouchers())
    }
}

export function removeVoucher(code) {
    return (dispatch, getState) => {
        const state: AppState = getState()
        const newVoucherState = state.joinevent.cart.vouchers.filter(
            (voucher) => voucher.code !== code
        )
        dispatch(updateVouchers(newVoucherState))
        // showToastSuccess(
        //     'The Code was removed from your cart and the total has been updated'
        // )
    }
}

export function addToBundle() {
    return (dispatch, getState) => {
        const state: AppState = getState()
        const cart = prepareCartObject(state)
        const user = state.user.data
        let item: BundledChallenge = {
            type: 'challenge',
            name: state.joinevent.summary.name,
            challengeUrl: state.joinevent.summary.challengeUrl,
            challengeId: cart.challengeId,
            challengeAthleteId: cart.challengeAthleteId, //Might not need this
            items: cart.items,
            imageUrl: state.joinevent.summary.imageUrl,
            minimumPrice: state.joinevent.summary.minimumPrice,
            price: state.joinevent.summary.price,
            addonDiscount: state.joinevent.summary.addonDiscount,
            waiverAccepted: state.joinevent.summary.waiverAccepted,
            discount: 0, //Put the actual discount in here if multiple items are selected
            itemTotal: 0, //Same for this
            //These next 2 properties are for sending to Branch on purchase
            logo: state.joinevent.summary.logo,
            fundraisingMessage: state.joinevent.summary.fundraisingMessage,
            isSwag: state.joinevent.summary.isSwag,
            is30Day: state.joinevent.summary.is30Day,
            sku: state.joinevent.summary.challengeId,
            key: null
        }

        //Get items without the one being added (in case it's an update)
        const challenges = state.joinevent.cart.bundle.challenges.filter(
            (i) => i.key !== cart.challengeId
        )

        // try {
        //     tagsAddToCart(item)
        // } catch (e) {
        //     // eslint-disable-next-line no-console
        //     console.error(e)
        // }

        item = {
            ...item,
            key: cart.challengeId
        }

        challenges.push(item)

        const newCartStateObj = {
            ...state.joinevent.cart,
            bundle: {
                ...state.joinevent.cart.bundle,
                challenges: challenges
            }
        }

        const addedChallenges = newCartStateObj.bundle.challenges.filter(
            (item) => !state.joinevent.cart.bundle.challenges.includes(item)
        )

        trackAddToCart(addedChallenges, user)
        dispatch(updateCart(newCartStateObj))
    }
}

export function mapRewardsSelections(selectedRewards) {
    return (dispatch, getState) => {
        const { joinevent }: AppState = getState()
        const rewards = joinevent.rewards
        let updatedRewards

        if (!selectedRewards.length || !selectedRewards) {
            updatedRewards = rewards.map((item) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return {
                    ...item,
                    selected: false
                }
            })
        }

        updatedRewards = rewards.map((topItem) => {
            if (
                selectedRewards.some(
                    (item) => item.challengeAddonId === topItem.challengeAddonId
                )
            ) {
                const mappedReward = selectedRewards.find(
                    (item) => item.challengeAddonId === topItem.challengeAddonId
                )
                const baseItem = {
                    ...topItem,
                    selected: true
                }

                // TODO: multiple quantity
                if (mappedReward.selectedOptions.length) {
                    baseItem.challengeAddonOptionId =
                        mappedReward.selectedOptions[0].challengeAddonOptionId
                    baseItem.challengeAddonOptionName =
                        mappedReward.selectedOptions[0].displayText
                }

                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return baseItem
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return {
                    ...topItem,
                    selected: false
                }
            }
        })

        dispatch(updateRewardSelections(updatedRewards))
    }
}

export function mapAddonsSelections(selectedAddons) {
    return (dispatch, getState) => {
        const { joinevent } = getState()
        const addons = joinevent.event.addons

        let updatedAddons

        if (!selectedAddons.length || !selectedAddons) {
            updatedAddons = addons.map((item) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return {
                    ...item,
                    selected: false
                }
            })
        }

        updatedAddons = addons.map((topItem) => {
            if (
                selectedAddons.some(
                    (item) => item.challengeAddonId === topItem.challengeAddonId
                )
            ) {
                const mappedReward = selectedAddons.find(
                    (item) => item.challengeAddonId === topItem.challengeAddonId
                )
                const baseItem = {
                    ...topItem,
                    selected: true
                }

                // TODO: multiple quantity
                if (mappedReward.selectedOptions.length) {
                    baseItem.challengeAddonOptionId =
                        mappedReward.selectedOptions[0].challengeAddonOptionId
                    baseItem.challengeAddonOptionName =
                        mappedReward.selectedOptions[0].displayText
                }

                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return baseItem
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return {
                    ...topItem,
                    selected: false
                }
            }
        })

        dispatch(updateAddonSelections(updatedAddons))
    }
}
